import { Button, DialogContent, DialogTitle } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { FileTag } from "../../../../api/types/fileTypes";
import { getEntityTitle } from "../../../common/files/fileDetailsHelper";
import TagEditForm from "./TagEditForm";
import type { TagFormState, TagSettingsDialogState } from "./dialogState";

interface Props {
  dialogState: TagSettingsDialogState;
  onFormChange: (form: TagFormState) => void;
  onCancel: () => void;
  onSubmit: (catalogueId: string, updatedTag: FileTag) => void;
}

const updateFileTag = withErrorHandling(adminApi.updateFileTag);

const EditTagDialog = ({ dialogState, onFormChange, onCancel, onSubmit }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!dialogState.form.isValid) {
      return;
    }

    const { catalogueId } = dialogState.form;
    if (!catalogueId || !dialogState.form.entityType) {
      return;
    }

    setSaving(true);
    const [resp, error] = await updateFileTag(catalogueId, { name: dialogState.form.name.trim() });
    if (error) {
      logError(error, "updateFileTag");
      sendNotificationError(error.message);
    } else {
      sendNotification(
        `Tag "${dialogState.form.name}" successfully updated for entity ${getEntityTitle(dialogState.form.entityType)}`
      );
      onSubmit(catalogueId, resp);
    }
    setSaving(false);
  };

  return (
    <SmallDialog open={dialogState.openDialog === "edit"} onClose={onCancel}>
      <DialogTitle>Edit Tag</DialogTitle>
      <DialogCloseButton onClick={onCancel} />

      <DialogContent sx={(t) => ({ px: t.spacing(4) })}>
        <TagEditForm form={dialogState.form} onChange={onFormChange} disableEntitySelection />
      </DialogContent>

      <DialogButtons onCancel={onCancel}>
        <Button
          disabled={!dialogState.form.isValid}
          variant="contained"
          color="primary"
          loading={saving}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default EditTagDialog;
